import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { generateColorMap, DEFAULT_CHART_COLOR } from './colorPalettes';
import { getChartColors, getChartTitle, getChartAxis, getChartLegend, getChartFont } from '../../utils/chartColors';
import './chartTitles.css';

// Helper function to convert hex color to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? 
    `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : 
    '0, 0, 0';
};

/**
 * Reusable Bar Chart Component
 * @param {Array} data - Array of data objects with x and y values
 * @param {string} xKey - Key for x-axis data
 * @param {string} yKey - Key for y-axis data
 * @param {string} groupKey - Optional key for multi-series grouping (e.g., 'ticker')
 * @param {string} title - Chart title
 * @param {string} xLabel - X-axis label
 * @param {string} yLabel - Y-axis label
 * @param {string} color - Bar color (single series only)
 * @param {number} opacity - Bar opacity (0-1)
 * @param {boolean} gradientOpacity - Enable gradient opacity from 20% base to 100% top
 * @param {boolean} formatCurrency - Add $ prefix to y-axis
 * @param {string} barmode - 'group' or 'stack' for multi-series
 */
const BarChart = ({ 
  data = [], 
  xKey = 'category', 
  yKey = 'value',
  groupKey = null,
  title = 'Bar Chart',
  xLabel = 'Category',
  yLabel = 'Value',
  color = DEFAULT_CHART_COLOR,
  opacity = 0.85,
  gradientOpacity = false,
  formatCurrency = false,
  barmode = 'group'
}) => {
  const [themeKey, setThemeKey] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Listen for theme changes and window resize
  useEffect(() => {
    const checkThemeChange = () => {
      setThemeKey(prev => prev + 1);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setThemeKey(prev => prev + 1); // Force re-render on resize
    };

    // Listen for DOM mutations (theme class changes)
    const observer = new MutationObserver(checkThemeChange);
    const landingPage = document.querySelector('.landing-page');
    if (landingPage) {
      observer.observe(landingPage, { attributes: true, attributeFilter: ['class'] });
    }

    // Listen for window resize
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  let plotData = [];

  if (groupKey && data.length > 0 && data[0][groupKey]) {
    // Multi-series support
    const groups = [...new Set(data.map(item => item[groupKey]))];
    const colorMap = generateColorMap(groups);

    groups.forEach(group => {
      const groupData = data.filter(item => item[groupKey] === group);
      const groupColor = colorMap[group] || DEFAULT_CHART_COLOR;
      
      plotData.push({
        x: groupData.map(item => item[xKey]),
        y: groupData.map(item => item[yKey]),
        name: group,
        type: 'bar',
        marker: {
          color: groupColor,
          opacity: opacity,
          line: {
            width: 0
          }
        },
        hovertemplate: `<b>${group}</b><br>${yLabel}: %{y}<br>${xLabel}: %{x}<extra></extra>`
      });
    });
  } else {
    // Single series
    const markerConfig = {
      color: color,
      opacity: opacity,
      line: {
        color: color,
        width: 1,
        opacity: opacity
      }
    };

    // Add gradient opacity if enabled
    if (gradientOpacity && data.length > 0) {
      // Create true vertical gradient using multiple overlapping bars
      const rgb = hexToRgb(color);
      const gradientSteps = 10; // More steps for smoother gradient
      
      // Clear existing plotData and create gradient layers
      plotData = [];
      
      for (let step = 0; step < gradientSteps; step++) {
        const opacity = 0.2 + (step / (gradientSteps - 1)) * 0.8; // 0.2 to 1.0
        const stepHeight = 1 / gradientSteps; // Height of this step
        const stepBase = step * stepHeight; // Base position of this step
        
        plotData.push({
          x: data.map(item => item[xKey]),
          y: data.map(item => item[yKey] * stepHeight),
          base: data.map(item => item[yKey] * stepBase),
          type: 'bar',
          marker: {
            color: `rgba(${rgb}, ${opacity})`,
            line: {
              color: `rgba(${rgb}, ${opacity})`,
              width: 0
            }
          },
          hovertemplate: step === gradientSteps - 1 ? `<b>${yLabel}</b>: %{y}<br><b>${xLabel}</b>: %{x}<extra></extra>` : '',
          showlegend: false,
          width: 0.8
        });
      }
    } else {
      // Regular single bar trace (no gradient)
      plotData = [{
        x: data.map(item => item[xKey]),
        y: data.map(item => item[yKey]),
        type: 'bar',
        marker: markerConfig,
        hovertemplate: `<b>${yLabel}</b>: %{y}<br><b>${xLabel}</b>: %{x}<extra></extra>`,
        showlegend: false
      }];
    }
  }

  // Sort plotData alphabetically by name for consistent legend ordering
  if (plotData.length > 1 && plotData.some(trace => trace.name)) {
    plotData.sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }

  const colors = getChartColors();
  const isDarkMode = !document.querySelector('.landing-page')?.classList.contains('light-mode');
  
  // Responsive font sizes
  let tickSize = 12;
  if (windowWidth <= 480) {
    tickSize = 9;
  } else if (windowWidth <= 768) {
    tickSize = 10;
  } else if (windowWidth <= 1024) {
    tickSize = 11;
  }

  const xAxisConfig = getChartAxis(xLabel, true);
  const yAxisConfig = getChartAxis(yLabel, false);
  
  // Add responsive tick font and currency prefix to axes
  xAxisConfig.tickfont = getChartFont(tickSize);
  yAxisConfig.tickfont = getChartFont(tickSize);
  yAxisConfig.tickprefix = formatCurrency ? '$' : '';
  if (yLabel) {
    yAxisConfig.title.standoff = 20;
  }

  const itemCount = groupKey && data.length > 0 && data[0][groupKey] 
    ? [...new Set(data.map(item => item[groupKey]))].length 
    : 0;

  const titleConfig = getChartTitle(title, 20);
  // Override title positioning to match original (x: 0, xanchor: 'left')
  titleConfig.x = 0;
  titleConfig.xanchor = 'left';
  titleConfig.y = undefined;
  titleConfig.yanchor = undefined;

  const layout = {
    title: titleConfig,
    paper_bgcolor: colors.background,
    plot_bgcolor: isDarkMode ? 'rgba(15,15,15,0.5)' : 'rgba(0,0,0,0)',
    xaxis: xAxisConfig,
    yaxis: yAxisConfig,
    margin: { 
      t: 80, 
      r: windowWidth <= 768 ? 20 : 40, 
      b: (() => {
        const isMobile = windowWidth <= 768;
        return isMobile ? (itemCount > 6 ? 140 : 120) : 40;
      })(), 
      l: 100 
    },
    hovermode: 'closest',
    barmode: barmode,
    legend: itemCount > 0 ? getChartLegend(itemCount, windowWidth) : {
      font: getChartFont(12),
      bgcolor: 'rgba(0,0,0,0)'
    },
    autosize: true
  };

  const config = {
    responsive: true,
    displayModeBar: false
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Plot
        key={themeKey}
        data={plotData}
        layout={layout}
        config={{
          ...config,
          responsive: true,
          useResizeHandler: true
        }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler={true}
      />
    </div>
  );
};

export default BarChart;


